import { Link } from 'react-router-dom';

import { crmFicheHref } from '../../../../supabase/functions/_shared/crmFiche';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useAccountDetailData } from '../../../hooks/useAccountDetailData';
import { useAccountSheet } from '../../../hooks/useAccountSheet';
import { newMissionHref } from '../../../lib/bureau/newMission';
import { exchangesOf, keyContacts, openOpportunities } from '../../../lib/crm/accountSheet';
import { useCanUseTelephony } from '../../../lib/telephonyAccess';
import { CallButton } from '../../telephony/CallButton';
import { EntityMissionsBlock } from '../EntityMissionsBlock';
import { AccountSheetHeader } from './AccountSheetHeader';
import { CompanyBlock, ContactsBlock, ExchangesBlock, OpportunitiesBlock } from './AccountSheetBlocks';
import { EncreSheet, SheetEmpty } from './EncreSheet';

/**
 * An account's working sheet, in Encre (PLAN-ENCRE-OUTILS, mock-up « Fiche » — Gilles, 12/10/2026:
 * the side sheet is the working sheet; the full record, with its editing and history, is one click
 * away). What one needs to act: who it is, what Otto does about it and asking him, the open deals,
 * the people to reach, the latest exchanges, what we know of the company. Opened from every name of
 * the app (`openAccountSidebar`).
 */
export function AccountSheet({ accountId, onClose }: { accountId: string | null; onClose: () => void }) {
  const { openOpportunitySidebar, openContactSidebar, openAppointmentSidebar, openAccountSidebar } = useSidebar();
  const canCall = useCanUseTelephony();
  const head = useAccountSheet(accountId);
  const { detailData: detail, ready, error } = useAccountDetailData(accountId);
  // Never « Aucun… » while the rows load, nor after they failed (relecture du 13/10/2026).
  const rows = { loading: !ready && !error, error };
  const account = head.account;
  const ficheHref = accountId ? crmFicheHref('account', accountId) : '/admin/crm/accounts';
  const contacts = keyContacts(detail.contacts);

  return (
    <EncreSheet open={accountId !== null} label={account ? `Fiche de ${account.name}` : 'Fiche du compte'} focusKey={accountId} onClose={onClose}>
      {!account ? (
        head.error ? (
          <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {head.error}</p>
        ) : head.missing ? (
          <SheetEmpty>Ce compte est introuvable : il a été supprimé, ou n'existe pas.</SheetEmpty>
        ) : (
          <div className="space-y-4 pt-2" aria-busy="true">
            <div className="h-14 w-2/3 animate-pulse rounded-2xl bg-desk-subtle" />
            <div className="h-24 animate-pulse rounded-2xl bg-desk-subtle" />
          </div>
        )
      ) : (
        <>
          <AccountSheetHeader
            account={account}
            badge={head.badge}
            call={canCall && account.phone ? <CallButton number={account.phone} accountId={account.id} /> : null}
            newMissionHref={newMissionHref({ client: account.id })}
            ficheHref={ficheHref}
            onNavigate={onClose}
          />
          <EntityMissionsBlock kind="account" entity={{ id: account.id, name: account.name }} />
          <OpportunitiesBlock opportunities={openOpportunities(detail.opportunities)} state={rows} onOpen={openOpportunitySidebar} />
          <ContactsBlock
            contacts={contacts}
            total={detail.contacts.length}
            state={rows}
            onOpen={openContactSidebar}
            more={
              <Link to={ficheHref} onClick={onClose} className="text-sm font-medium text-desk-accent-text hover:underline">
                Tout voir
              </Link>
            }
          />
          <ExchangesBlock
            exchanges={exchangesOf(detail.appointments, detail.notes)}
            state={rows}
            onOpenAppointment={openAppointmentSidebar}
            noteHref={(id) => `/admin/notes?note=${encodeURIComponent(id)}`}
            onNavigate={onClose}
          />
          <CompanyBlock account={account} parent={head.parent} onOpenAccount={openAccountSidebar} />
        </>
      )}
    </EncreSheet>
  );
}
